import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, formatApiError } from "@/lib/api";
import { Field, Panel } from "./Fields";

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [me, setMe] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [pw, setPw] = useState({ current_password: "", new_password: "" });

  const load = useCallback(() => {
    api.get("/admin/users").then((r) => { setUsers(r.data.users); setMe(r.data.me); }).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    try {
      await api.post("/admin/users", form);
      toast.success("Admin added");
      setForm({ name: "", email: "", password: "" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Remove this admin?")) return;
    try { await api.delete(`/admin/users/${id}`); toast.success("Admin removed"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const changePw = async () => {
    try { await api.post("/admin/change-password", pw); toast.success("Password changed"); setPw({ current_password: "", new_password: "" }); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <Panel title={`Admin Users (${users.length})`} description="Admins can log in at /admin/login and edit the whole website.">
        <ul className="divide-y divide-slate-100 mb-5">
          {users.map((u) => (
            <li key={u.id} data-testid={`admin-user-${u.id}`} className="flex items-center justify-between py-3">
              <div>
                <p className="font-semibold text-slate-900 text-sm">{u.name} {u.id === me && <span className="text-xs text-emerald-700 font-medium ml-1">(you)</span>}</p>
                <p className="text-xs text-slate-500">{u.email}</p>
              </div>
              {u.id !== me && (
                <Button size="icon" variant="ghost" data-testid={`admin-user-delete-${u.id}`} className="h-8 w-8 text-red-600" onClick={() => remove(u.id)} aria-label="Remove admin"><Trash2 className="w-4 h-4" /></Button>
              )}
            </li>
          ))}
        </ul>
        <h3 className="font-semibold text-slate-900 text-sm mb-3">Add new admin</h3>
        <div className="space-y-3">
          <Field label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="new-admin-name" />
          <Field label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="new-admin-email" />
          <Field label="Password (min 8 chars)" type="password" value={form.password} onChange={(v) => setForm({ ...form, password: v })} testid="new-admin-password" />
          <Button data-testid="new-admin-submit" onClick={add} disabled={!form.name || !form.email || form.password.length < 8} className="rounded-xl bg-slate-900 hover:bg-slate-800 text-white">Add Admin</Button>
        </div>
      </Panel>
      <Panel title="Change My Password">
        <div className="space-y-3">
          <Field label="Current password" type="password" value={pw.current_password} onChange={(v) => setPw({ ...pw, current_password: v })} testid="pw-current" />
          <Field label="New password (min 8 chars)" type="password" value={pw.new_password} onChange={(v) => setPw({ ...pw, new_password: v })} testid="pw-new" />
          <Button data-testid="pw-submit" onClick={changePw} disabled={!pw.current_password || pw.new_password.length < 8} className="rounded-xl bg-emerald-700 hover:bg-emerald-800 text-white">Update Password</Button>
        </div>
      </Panel>
    </div>
  );
}
